import { Loading } from 'common/ui/Loading';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { useUser } from 'core/stores/hooks/useUser';
import { useInitializeCampaignMutation } from 'generated-graphql/hooks/apollo-hooks';
import { CampaignType, ClosingMethod } from 'generated-graphql/types';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { useEffect, useRef } from 'react';
import { useToast, ToastType } from 'services/toast';
import { useNavigate } from 'react-router-dom';
import { Paths } from 'services/navigation/paths';
import { createAutomationTasksV2 } from 'pages/CampaignCreation/utils/createAutomationTasksV2';
import { mapQuestionsPayloadV3 } from 'modules/campaign-creation/utils/mapQuestionsPayloadV3';
import { Stack } from '@mui/material';
import { useSelectedCandidateBatchStatusesHandler } from '../hooks/useSelectedCandidateBatchStatusesHandler';
import { campaignCreationGetATSJobId } from 'modules/campaign-creation/utils/campaignCreationGetATSJobId';
import { launchCampaign } from 'services/campaign/api';
import { useConfetti } from 'utils/confetti';

export const CampaignCreationCreateStep: React.FC = () => {
  const navigate = useNavigate();
  const user = useUser();
  const organization = useOrganization();
  const organizationConfig = useOrganizationConfig();
  const campaignId = useCampaignCreationStore((store) => store.campaignId);
  const state = useCampaignCreationStore((store) => store.state);
  const onCampaignCreateError = useCampaignCreationStore(
    (store) => store.onCampaignCreateError
  );
  const { showToast } = useToast();
  const { triggerConfetti } = useConfetti();
  const {
    candidateBatchStatuses,
    loading: selectedCandidateBatchStatusesLoading
  } = useSelectedCandidateBatchStatusesHandler();

  const [initializeCampaign] = useInitializeCampaignMutation();

  // Track if campaign creation has already been initiated
  const hasExecuted = useRef(false);

  useEffect(() => {
    const createCampaign = async () => {
      if (selectedCandidateBatchStatusesLoading) {
        return;
      }

      if (hasExecuted.current) {
        return;
      }
      // Lock immediately to prevent race conditions from rapid re-renders
      hasExecuted.current = true;

      // Track if campaign was created so we know whether retries are safe
      let campaignCreated = false;

      try {
        // NOTE: This should never happen, it's for the type safety
        if (!state || !state.setupAndSimulationFields) {
          showToast({
            message: 'Something went wrong. Please try again later.',
            type: ToastType.Error
          });
          onCampaignCreateError();
          return;
        }

        const jobDescriptionText =
          state.noATSJobDescriptionState?.jobDescriptionText ||
          state.ATSJobDescriptionState?.selectedJob.jobDescriptionText;

        if (
          !state.campaignType ||
          !state.setupAndSimulationFields.agentId ||
          !state.questions ||
          !state.channel
        ) {
          showToast({
            message: 'Missing fields.',
            type: ToastType.Error
          });
          onCampaignCreateError();
          return;
        }

        // Check the campaign title exists, otherwise the user won't be able to select the campaign
        if (
          state.campaignType === CampaignType.EngagementOutreach &&
          !state.engagementDescriptionState?.title
        ) {
          showToast({
            message: 'Missing title.',
            type: ToastType.Error
          });
          onCampaignCreateError();
          return;
        } else if (
          state.campaignType !== CampaignType.EngagementOutreach &&
          !state.jobDescriptionParsedState?.jobDescriptionParsed.jobTitle
        ) {
          showToast({
            message: 'Missing job title.',
            type: ToastType.Error
          });
          onCampaignCreateError();
          return;
        }

        const atsJobId = campaignCreationGetATSJobId({
          atsJobId: state.ATSJobDescriptionState?.selectedJob?.atsJobId || null,
          candidateBatchStatuses,
          csvS3Key: state.CSVImportState?.csvS3Key || null,
          atsListId: state.ATSImportState?.atsListId || null
        });

        // Get launch configuration from store
        const launchConfig = state.launchConfig;

        // NOTE: Explict with the nulls here.
        const result = await initializeCampaign({
          variables: {
            input: {
              campaignId: campaignId || null,
              // CORE
              userId: user.id,
              organizationId: organization.id,
              // CHOOSE_TYPE STEP
              type: state.campaignType,
              // ENGAGEMENT_DESCRIPTION STEP
              outreachDescription:
                state.engagementDescriptionState?.outreachDescription || null,
              title:
                state.engagementDescriptionState?.title ||
                state.jobDescriptionParsedState?.jobDescriptionParsed
                  ?.jobTitle ||
                null,
              // JOB_DESCRIPTION STEP
              jobDescriptionText,
              // NO_ATS_JOB_DESCRIPTION STEP
              externalId: state.noATSJobDescriptionState?.externalId || null,
              // ATS_JOB_DESCRIPTION STEP
              atsJobId,
              atsJobPostingId:
                state.ATSJobDescriptionState?.selectedJob.atsJobPostingId ||
                null,
              // EDIT_JOB_DESCRIPTION STEP
              jobDescriptionParsed:
                state.jobDescriptionParsedState?.jobDescriptionParsed,
              additionalContext:
                state.jobDescriptionParsedState?.additionalContext || null,
              // ATS_IMPORT_CANDIDATES STEP
              atsListId: state.ATSImportState?.atsListId || null,
              // CSV_IMPORT_CANDIDATES STEP
              csvS3Key: state.CSVImportState?.csvS3Key || null,
              // QUESTIONS_AND_DOCUMENTS STEP
              questions: mapQuestionsPayloadV3(state.questions),
              // CHANNEL STEP
              channel: state.channel,
              // null = inherit the org-level fallback setting
              emailChannelFallbackEnabled:
                state.emailChannelFallbackEnabled ?? null,
              // LANGUAGE
              language:
                state.language ||
                organizationConfig?.availableLanguages?.[0] ||
                'en_us',
              // SETUP_AND_SIMULATION STEP
              agentId: state.setupAndSimulationFields.agentId,
              // TODO: Fix this type
              closingMethod: state.setupAndSimulationFields
                .closingMethod as unknown as ClosingMethod,
              templateId: state.setupAndSimulationFields.templateId || null,
              customTemplatedMessage:
                state.setupAndSimulationFields.customTemplatedMessage || null,
              customTemplatedSubjectLine:
                state.setupAndSimulationFields.customTemplatedSubjectLine ||
                null,
              calendarMeetingTemplateId:
                state.setupAndSimulationFields.calendarMeetingTemplateId ||
                null,
              customRequest:
                state.setupAndSimulationFields.customRequest || null,
              rejectionMessage:
                state.setupAndSimulationFields.rejectionMessage || null,
              closingMessage:
                state.setupAndSimulationFields.closingMessage || null,
              timeToAutoCloseConversationsInHours:
                state.setupAndSimulationFields
                  .timeToAutoCloseConversationsInHours ?? null,
              disableNudging:
                state.setupAndSimulationFields.disableNudging ?? false,
              closePreviousConversations:
                state.setupAndSimulationFields.closePreviousConversations
            }
          }
        });

        const newCampaignId = result.data?.initializeCampaign?.id;

        if (!newCampaignId) {
          showToast({
            message: 'Failed to create campaign. Please try again.',
            type: ToastType.Error
          });
          onCampaignCreateError();
          return;
        }

        // Campaign was successfully created - don't allow retries that could create duplicates
        campaignCreated = true;

        await createAutomationTasksV2(state.automationTasks || [], {
          organizationId: organization.id,
          campaignId: newCampaignId
        });

        // Handle launch if configured
        if (launchConfig?.launchNow || launchConfig?.launchDate) {
          // Only call launchCampaign for CSV campaigns which have immediate candidates.
          // ATS campaigns (list and job) receive candidates asynchronously via sync/webhook,
          // so startSendingMessages would fail if called now. The backend handles sending
          // for ATS campaigns once candidates are available.
          const hasImmediateCandidates = !!state.CSVImportState?.csvS3Key;

          if (hasImmediateCandidates) {
            try {
              await launchCampaign({
                campaignId: newCampaignId,
                launchScheduleDateTime: launchConfig.launchDate?.toISOString()
              });
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Failed to launch campaign:', error);
              showToast({
                message:
                  'Campaign created but failed to launch. Please try again from the campaign page.',
                type: ToastType.Error
              });
              navigate(`${Paths.OUTBOUND_CAMPAIGN}/${newCampaignId}`);
              return;
            }
          }

          // Show success message (campaign is live either way)
          if (launchConfig.launchNow) {
            triggerConfetti();
            showToast({
              message: 'Your campaign is live!',
              type: ToastType.Success
            });
          } else {
            showToast({
              message: 'Campaign scheduled successfully!',
              type: ToastType.Success
            });
          }

          navigate(`${Paths.OUTBOUND_CAMPAIGN}/${newCampaignId}`);
          return;
        }

        // If the campaign is created from the ATS, we don't want to generate the messages
        if (
          state.ATSImportState?.atsListId ||
          state.ATSJobDescriptionState?.selectedJob?.atsJobId ||
          !state.CSVImportState?.csvS3Key
        ) {
          navigate(`${Paths.OUTBOUND_CAMPAIGN}/${newCampaignId}`);
          return;
        }

        navigate(
          `${Paths.CAMPAIGN_OUTBOUND_OPENING_MESSAGES}/${newCampaignId}`
        );
      } catch (error) {
        // Only allow retries if campaign wasn't created yet
        // If campaign was created but a later step failed, don't retry to avoid duplicates
        if (!campaignCreated) {
          hasExecuted.current = false;
        }
        showToast({
          message:
            error instanceof Error
              ? error.message
              : 'The campaign could not be processed. Try again later.',
          type: ToastType.Error
        });
        onCampaignCreateError();
      }
    };

    createCampaign();
  }, [selectedCandidateBatchStatusesLoading]);

  return (
    <Stack width="100%" justifyContent="center" alignItems="center">
      <Loading />
    </Stack>
  );
};
